import { zodResolver } from '@hookform/resolvers/zod'
import {
  Cancel as CancelIcon,
  Google as GoogleIcon,
  LockReset as LockResetIcon,
} from '@mui/icons-material'
import {
  Alert,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Collapse,
  Divider,
  Link,
  Modal,
  Stack,
  Typography,
} from '@mui/material'
import { HttpStatusCode } from 'axios'
import { PhoneNumberUtil } from 'google-libphonenumber'
import { useSnackbar } from 'notistack'
import { useForm } from 'react-hook-form'
import {
  PasswordElement,
  SelectElement,
  TextFieldElement,
} from 'react-hook-form-mui'
import { PatternFormat } from 'react-number-format'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'

import {
  changePasswordBySMSCode,
  checkSessionToken,
  resetPassword,
  resetPasswordBySMS,
  resetPasswordService,
} from '~/api'
import animationData from '~/assets/ilustracion_loading.json'
import face from '~/assets/serious_face.png'
import { axios, serverHost } from '~/axios'
import PhoneNumberInput from '~/components/inputs/PhoneNumberInput'
import StrongPasswordCriterias from '~/components/shared/StrongPasswordCriterias'
import ForgotUsernameDialog from '~/features/Auth/components/ForgotUsernameDialog'
import authFieldSx from '~/features/Auth/components/auth-field-sx'
import LoginLayout from '~/features/Auth/components/LoginLayout'
import MFAScreen from '~/features/Auth/views/MFA-screen'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { updateUser } from '~/redux/slices/user'
import {
  exchangeSamlCode,
  getSsoLoginUrl,
  getToken,
  logoutWithToken,
  ssoLogin,
} from '~/services/auth-services'
import { getFormattedPhoneNumber } from '~/utils/form-utils'
import { op } from '~/utils/open-panel-utils'
import { ROLES } from '~/utils/role-utils'
import { type Environment, type User } from '~/utils/types/user'

import Lottie from '../features/Auth/components/Lottie'

const enviromentsList = ['platform', 'demo', 'testing'] as const
const websiteEnv: (typeof enviromentsList)[number] =
  enviromentsList.find(env => serverHost.includes(env)) ?? 'testing'

const cognitoConfig: Record<
  typeof websiteEnv,
  { domain: string; webClientId: string }
> = {
  testing: {
    domain: 'https://auth.testing.povertystoplight.org',
    webClientId: '2k7cqlhhm5sm2mm4uq76ju8lcu',
  },
  demo: {
    domain: 'https://auth.demo.povertystoplight.org',
    webClientId: '7c03of66funuqe4ceun99b890c',
  },
  platform: {
    domain: 'https://auth.platform.povertystoplight.org',
    webClientId: '4940bu07rkqsn0oudqo5tfol8r',
  },
}

const mainPagesByRole = {
  [ROLES.SURVEY_TAKER]: '/surveys',
  [ROLES.FACILITATOR]: '/surveys',
  [ROLES.STAKEHOLDER]: '/solutions',
  [ROLES.FAMILY]: '/my-profile',
} as const

const phoneUtil = PhoneNumberUtil.getInstance()

type RetrievedUser = Awaited<ReturnType<typeof getToken>>['data']['user']

interface FinalizeLoginArgs {
  user: User | null
  retrievedUser: RetrievedUser
  accessToken: string
  refreshToken: string
  refreshTokenExpiresAt: number
  env: string
  returnPath: string
  dispatch: ReturnType<typeof useAppDispatch>
  navigate: ReturnType<typeof useNavigate>
}

interface MfaLoginState {
  returnPath: string
  accessToken: string
  refreshToken: string
  refreshTokenExpiresAt: number
  env: string
  retrievedUser: RetrievedUser
}

// Module-level (not component-scoped) so a StrictMode dev-mode render
// replay — which can momentarily mount separate component instances, each
// with its own fresh useRef — can't defeat the single-fire guard below. A
// real SSO round-trip is a full page navigation, so this naturally resets
// before every new attempt.
let lastExchangedSsoCode: string | null = null
let lastHandledSsoError: string | null = null

function getSsoRedirectUri(clientType: SsoClientType) {
  const path = clientType === 'saml' ? '/saml-login' : '/login'
  return `${window.location.origin}${path}`
}

type SsoClientType = 'web' | 'saml'

/**
 * The backend suffixes the `state` it round-trips through Cognito with a
 * `|ct:web` or `|ct:saml` marker identifying which Cognito app client issued
 * the code, so the frontend knows which client to exchange it with. An
 * absent state means an IdP-initiated login (e.g. the Google Workspace app
 * launcher), which only the SAML client can receive.
 */
function parseSsoState(rawState: string | null): {
  clientType: SsoClientType
  returnPath: string | null
} {
  if (!rawState) return { clientType: 'saml', returnPath: null }
  const parts = rawState.split('|')
  const ctPart = parts.find(p => p.startsWith('ct:'))
  const clientType: SsoClientType = ctPart === 'ct:saml' ? 'saml' : 'web'
  const returnPath = parts.filter(p => !p.startsWith('ct:')).join('|') || null
  return { clientType, returnPath }
}

interface CompleteLoginArgs {
  user: User | null
  retrievedUser: RetrievedUser
  accessToken: string
  refreshToken: string
  refreshTokenExpiresAt: number
  env: string
  returnPath: string
  dispatch: ReturnType<typeof useAppDispatch>
  navigate: ReturnType<typeof useNavigate>
  onMFARequired: (state: MfaLoginState) => void
}

/**
 * Shared by password login and SSO exchange: gates on MFA (org admins with
 * the feature flag enabled) before handing off to finalizeLogin, so both
 * flows stay in sync.
 */
async function completeLogin({
  user,
  retrievedUser,
  accessToken,
  refreshToken,
  refreshTokenExpiresAt,
  env,
  returnPath,
  dispatch,
  navigate,
  onMFARequired,
}: CompleteLoginArgs) {
  const userRole = retrievedUser.authorities[0].authority
  const featureFlags =
    (retrievedUser.organization?.featureFlags as string[] | undefined) ?? []
  const requiresMfaByRole = userRole === ROLES.ORGANIZATION_ADMIN
  const mfaEnabled = requiresMfaByRole && featureFlags.includes('mfaEnabled')

  if (mfaEnabled) {
    // Check /validate endpoint to see if device is remembered
    try {
      const validateResponse = await checkSessionToken(accessToken)
      const rememberMfaCode = validateResponse.data.rememberMfaCode

      if (rememberMfaCode !== true) {
        onMFARequired({
          returnPath,
          accessToken,
          refreshToken,
          refreshTokenExpiresAt,
          env,
          retrievedUser,
        })
        return
      }
    } catch {
      // If validate fails, proceed with MFA to be safe
      onMFARequired({
        returnPath,
        accessToken,
        refreshToken,
        refreshTokenExpiresAt,
        env,
        retrievedUser,
      })
      return
    }
  }

  finalizeLogin({
    user,
    retrievedUser,
    accessToken,
    refreshToken,
    refreshTokenExpiresAt,
    env,
    returnPath,
    dispatch,
    navigate,
  })
}

/**
 * Persists the authenticated user into Redux and navigates to the post-login
 * destination. Shared by both the standard login path and the MFA-verified
 * path so the user-shape stays in sync between flows.
 */
function finalizeLogin({
  user,
  retrievedUser,
  accessToken,
  refreshToken,
  refreshTokenExpiresAt,
  env,
  returnPath,
  dispatch,
  navigate,
}: FinalizeLoginArgs) {
  dispatch(
    // @ts-expect-error Email comes as `string | undefined` from API
    updateUser({
      ...user,
      ...retrievedUser,
      id: retrievedUser.userId,
      token: accessToken,
      refreshToken,
      refreshTokenExpiresAt,
      env: env as Environment,
      willExpire: retrievedUser.willExpire,
      didExpire: retrievedUser.didExpire,
      daysToExpirePassword: retrievedUser.daysToExpirePassword,
      sso: retrievedUser.sso,
    }),
  )

  navigate(returnPath, { replace: true })
}

export default function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()

  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const user = useAppSelector(state => state.user)
  const [searchParams] = useSearchParams()

  const [mfaState, setMfaState] = useState<MfaLoginState | null>(null)

  const platform = websiteEnv === 'platform' ? 'demo' : 'platform'

  const ssoCode = searchParams.get('code')
  const samlCode = searchParams.get('saml_code')
  const [isSsoExchanging, setIsSsoExchanging] = useState(!!ssoCode || !!samlCode)
  const ssoError = searchParams.get('error')
  const idpParam = searchParams.get('idp')

  useEffect(() => {
    if (user?.token) return
    const targetURL = location.pathname

    if (
      targetURL === '/' ||
      targetURL.includes('/login') ||
      targetURL.includes('/saml-login')
    )
      return

    navigate(`/login?returnURL=${targetURL}`, { replace: true })
  }, [location, navigate, user])

  useEffect(() => {
    if (!idpParam || ssoCode) return
    if (user?.token) {
      const userRole = user.role as keyof typeof mainPagesByRole | undefined
      const nextPath = String(
        mainPagesByRole[userRole ?? ''] ?? '/homepage',
      )
      navigate(nextPath, {
        replace: true,
      })
      return
    }
    const hintParam = searchParams.get('hint')
    const { domain, webClientId } = cognitoConfig[websiteEnv]
    let authorizeUrl =
      `${domain}/oauth2/authorize` +
      `?client_id=${webClientId}` +
      `&response_type=code` +
      `&scope=email+openid+profile` +
      `&redirect_uri=${encodeURIComponent(getSsoRedirectUri('web'))}` +
      `&identity_provider=Google` +
      `&state=ct%3Aweb`
    if (hintParam) {
      authorizeUrl += `&login_hint=${encodeURIComponent(hintParam)}&prompt=login`
    }
    window.location.replace(authorizeUrl)
  }, [idpParam, navigate, searchParams, ssoCode, user])

  useEffect(() => {
    if (!samlCode) return
    navigate('/login', { replace: true })
    setIsSsoExchanging(true)

    void (async () => {
      try {
        const response = await exchangeSamlCode(samlCode)
        const retrievedUser = response.data.user
        const userRole = retrievedUser.authorities[0].authority
        const returnPath = mainPagesByRole[userRole] ?? '/homepage'

        await completeLogin({
          user,
          retrievedUser,
          accessToken: response.data.access_token,
          refreshToken: response.data.refresh_token,
          refreshTokenExpiresAt: response.data.refresh_token_expires_at,
          env: response.data.env,
          returnPath,
          dispatch,
          navigate,
          onMFARequired: setMfaState,
        })
      } catch {
        enqueueSnackbar(t('views.login.connectionError'), { variant: 'error' })
        navigate('/login', { replace: true })
      } finally {
        setIsSsoExchanging(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [samlCode])

  useEffect(() => {
    if (!ssoCode) return
    // The authorization code is single-use, so a StrictMode dev-mode render
    // replay must not send it to the exchange endpoint twice. There is no
    // effect cleanup/cancellation here: once the exchange has fired, we
    // always want to act on its result.
    if (lastExchangedSsoCode === ssoCode) return
    lastExchangedSsoCode = ssoCode

    const { clientType, returnPath: parsedReturnPath } = parseSsoState(
      searchParams.get('state'),
    )

    const redirectUri = getSsoRedirectUri(clientType)

    // The authorization code is single-use. Remove it from the address bar
    // and browser history before continuing so cancelling MFA or refreshing
    // the page cannot attempt to exchange the same code again.
    navigate('/login', { replace: true })

    setIsSsoExchanging(true)

    void (async () => {
      try {
        const response = await ssoLogin(ssoCode, redirectUri, clientType)

        const retrievedUser = response.data.user
        const userRole = retrievedUser.authorities[0].authority
        const mainPageByRole = mainPagesByRole[userRole] ?? '/homepage'
        const returnPath = parsedReturnPath ?? mainPageByRole

        await completeLogin({
          user,
          retrievedUser,
          accessToken: response.data.access_token,
          refreshToken: response.data.refresh_token,
          refreshTokenExpiresAt: response.data.refresh_token_expires_at,
          env: response.data.env,
          returnPath,
          dispatch,
          navigate,
          onMFARequired: setMfaState,
        })
      } catch {
        const message = t('views.login.ssoError')
        enqueueSnackbar(message, { variant: 'error' })
        navigate('/login', { replace: true })
      } finally {
        setIsSsoExchanging(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssoCode])

  useEffect(() => {
    if (!ssoError) return
    // Guard against a StrictMode dev-mode render replay showing the
    // snackbar twice.
    if (lastHandledSsoError === ssoError) return
    lastHandledSsoError = ssoError

    const errorDescription = searchParams.get('error_description') ?? ''
    console.error('SSO login error:', ssoError, errorDescription)

    const message =
      errorDescription.includes('not authorized for SSO') ||
      errorDescription.includes('No SSO-enabled user found')
        ? t('views.login.ssoNotAuthorized')
        : t('views.login.ssoError')

    enqueueSnackbar(message, { variant: 'error' })
    navigate('/login', { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssoError])

  // ── SSO exchange in progress ──────────────────────────────────────────────
  // Also gate on ssoCode and idpParam so the login form never flashes before
  // the OIDC redirect fires or while the code is being exchanged.
  if (isSsoExchanging || !!ssoCode || (!!idpParam && !user?.token)) {
    return (
      <Stack
        flex={1}
        alignItems="center"
        justifyContent="center"
        sx={{ minHeight: '100vh' }}
      >
        <Lottie
          options={{
            loop: true,
            autoplay: true,
            animationData,
            rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
          }}
          height={300}
          width={300}
        />
      </Stack>
    )
  }

  // ── MFA full-page screen ─────────────────────────────────────────────────
  if (mfaState) {
    return (
      <MFAScreen
        accessToken={mfaState.accessToken}
        email={mfaState.retrievedUser.email}
        phoneNumber={mfaState.retrievedUser.phoneNumber}
        onVerified={() => {
          finalizeLogin({
            user,
            retrievedUser: mfaState.retrievedUser,
            accessToken: mfaState.accessToken,
            refreshToken: mfaState.refreshToken,
            refreshTokenExpiresAt: mfaState.refreshTokenExpiresAt,
            env: mfaState.env,
            returnPath: mfaState.returnPath,
            dispatch,
            navigate,
          })
        }}
        onCancel={() => {
          void (async () => {
            try {
              await logoutWithToken(mfaState.refreshToken)
            } catch (error) {
              console.error('Error during logout:', error)
            } finally {
              setMfaState(null)
            }
          })()
        }}
      />
    )
  }

  return (
    <LoginLayout
      title={
        websiteEnv === 'platform'
          ? t('views.login.welcomeToPlatform')
          : t('views.login.welcomeToDemo')
      }
      environment={platform}
      environmentHref={`https://${platform}.povertystoplight.org/`}
      switchLabel={t('views.login.switchTo')}
    >
      <LoginForm onMFARequired={setMfaState} />
    </LoginLayout>
  )
}

function LoginForm({
  onMFARequired,
}: {
  onMFARequired: (state: MfaLoginState) => void
}) {
  const { t } = useTranslation()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [changePasswordModalData, setChangePasswordModalData] = useState<{
    isOpen: boolean
    userId: number | null
    phoneNumber: string | null
  }>({
    userId: null,
    isOpen: true,
    phoneNumber: null,
  })

  const [isForgotUsernameOpen, setIsForgotUsernameOpen] = useState(false)
  const [isSsoRedirecting, setIsSsoRedirecting] = useState(false)

  const [searchParams] = useSearchParams()

  const id = searchParams.get('id')
  const token = searchParams.get('token')

  const isResettingPassword = !!token || !!id
  const user = useAppSelector(state => state.user)

  const verificationMethodOptions = [
    { id: 'EMAIL', label: t('views.family.email') },
    { id: 'PHONE', label: t('views.family.phone') },
  ]

  type FormValues = z.infer<typeof formSchema>
  const formSchema = useMemo(() => {
    const requiredError = t('validation.fieldIsRequired')

    const loginSchema = z.object({
      formMode: z.literal('login'),
      username: z.string({ required_error: requiredError }).trim().min(1),
      password: z.string({ required_error: requiredError }).min(1),
    })

    const forgotPasswordSchema = z.object({
      formMode: z.literal('forgotPassword'),
      credentialMode: z.enum(['EMAIL', 'PHONE']),
      username: z.string({ required_error: requiredError }).trim().min(1),
      email: z
        .string()
        .email({ message: t('views.user.form.emailNotValid') })
        .trim()
        .min(1)
        .toLowerCase()
        .optional(),
      phoneCode: z
        .object({
          code: z.string(),
          id: z.string(),
          label: z.string(),
        })
        .optional(),
      phoneNumber: z.string().nullable().optional(),
    })

    const resetPasswordSchema = z.object({
      formMode: z.literal('resetPassword'),
      isPasswordValid: z.boolean().default(false),
      newPassword: z.string({ required_error: requiredError }).min(1),
      newPasswordConfirm: z.string({ required_error: requiredError }).min(1),
    })

    return z
      .discriminatedUnion('formMode', [
        loginSchema,
        forgotPasswordSchema,
        resetPasswordSchema,
      ])
      .superRefine((values, ctx) => {
        if (values.formMode === 'forgotPassword') {
          if (values.credentialMode === 'EMAIL') {
            if (!values.email) {
              ctx.addIssue({
                code: 'custom',
                path: ['email'],
                message: requiredError,
              })
              return z.NEVER
            }
          }

          if (values.credentialMode === 'PHONE') {
            const { phoneCode, phoneNumber } = values
            if (!phoneCode) {
              ctx.addIssue({
                code: 'custom',
                path: ['phoneCode'],
                message: requiredError,
              })
              return z.NEVER
            }

            if (!phoneNumber) {
              ctx.addIssue({
                code: 'custom',
                path: ['phoneNumber'],
                message: requiredError,
              })
              return z.NEVER
            }

            try {
              const international = `+${phoneCode.id} ${phoneNumber}`
              const phone = phoneUtil.parse(international, phoneCode.code)
              const isValid = phoneUtil.isValidNumberForRegion(
                phone,
                phoneCode.code,
              )

              if (!isValid) throw new Error('Invalid Number')
            } catch {
              ctx.addIssue({
                code: 'custom',
                message: t('validation.validPhoneNumber'),
                path: ['phoneNumber'],
              })
            }
          }
        }

        if (values.formMode === 'resetPassword') {
          const { newPassword, newPasswordConfirm, isPasswordValid } = values
          if (newPassword !== newPasswordConfirm) {
            ctx.addIssue({
              code: 'custom',
              path: ['newPasswordConfirm'],
              message: t('views.user.form.passwordConfirmFailed'),
            })

            return z.NEVER
          }

          if (!isPasswordValid) {
            ctx.addIssue({
              code: 'custom',
              message: t('views.user.form.passwordIsNotStrong'),
              path: ['newPassword'],
            })
            return z.NEVER
          }
        }
      })
  }, [t])

  const form = useForm<FormValues>({
    shouldUnregister: true,
    resolver: zodResolver(formSchema),
  })

  useEffect(() => {
    form.setValue('formMode', isResettingPassword ? 'resetPassword' : 'login')
  }, [isResettingPassword, form])

  function handleFormSublink() {
    const isNotOnLoginForm = form.watch('formMode') !== 'login'

    if (isNotOnLoginForm) {
      form.setValue('formMode', 'login')
      return
    }

    if (isResettingPassword) {
      form.setValue('formMode', 'resetPassword')
      return
    }

    form.setValue('formMode', 'forgotPassword')
  }

  async function onSubmit(formValues: FormValues) {
    closeSnackbar()
    const formData = new FormData()

    if (formValues.formMode === 'login') {
      const { username, password } = formValues

      formData.set('username', username)
      formData.set('password', password)
      formData.set('clientType', 'barClientIdPassword')

      try {
        const response = await getToken(formData)

        const retrievedUser = response.data.user
        const userRole = retrievedUser.authorities[0].authority

        if (retrievedUser.didExpire) {
          enqueueSnackbar(t('views.login.passwordExpired'), {
            variant: 'error',
            persist: true,
          })

          form.setValue('formMode', 'forgotPassword')
          form.setFocus('email')

          return
        }

        if (retrievedUser.willExpire) {
          const message =
            retrievedUser.daysToExpirePassword === 1
              ? t('views.login.passwordWillExpire_one', {
                count: retrievedUser.daysToExpirePassword,
              })
              : t('views.login.passwordWillExpire_other', {
                count: retrievedUser.daysToExpirePassword,
              })

          enqueueSnackbar(message, {
            variant: 'passwordWarning',
            persist: true,
          })
        }

        const parsedURL = searchParams.get('returnURL')
        const mainPageByRole = mainPagesByRole[userRole] ?? '/homepage'
        const returnPath = (parsedURL ?? mainPageByRole) as string

        await completeLogin({
          user,
          retrievedUser,
          accessToken: response.data.access_token,
          refreshToken: response.data.refresh_token,
          refreshTokenExpiresAt: response.data.refresh_token_expires_at,
          env: response.data.env,
          returnPath,
          dispatch,
          navigate,
          onMFARequired,
        })
      } catch (error) {
        let errorMessage: null | string = null

        if (error.toString().includes('401'))
          errorMessage = t('views.login.wrongCredentials')
        if (error.toString().includes('403'))
          errorMessage = t('views.login.inactiveUser')
        if (errorMessage === null)
          errorMessage = t('views.login.connectionError')

        enqueueSnackbar(errorMessage, { variant: 'error' })
      }

      return
    }

    if (formValues.formMode === 'forgotPassword') {
      const { credentialMode, username, email, phoneCode, phoneNumber } =
        formValues

      if (credentialMode === 'EMAIL' && email) {
        formData.set('email', email)
        formData.set('username', username)

        try {
          await resetPasswordService(formData)
          void op.track('reset_password_email_sent', { email, username })
          form.setValue('formMode', 'login')
          form.setValue('username', '')

          enqueueSnackbar(t('views.login.mailResetSuccess'), {
            variant: 'success',
          })
        } catch (error) {
          void op.track('reset_password_email_error', {
            email,
            username,
            error: String(error),
          })
          enqueueSnackbar(t('views.login.mailNotFound'), { variant: 'error' })
        }
      }

      if (credentialMode === 'PHONE' && phoneNumber && phoneCode) {
        const formattedPhoneNumber = getFormattedPhoneNumber({
          phoneCode,
          phoneNumber,
        })
        formData.set('username', username)
        formData.set('phoneNumber', formattedPhoneNumber)

        try {
          const response = await resetPasswordBySMS(formData)
          void op.track('reset_password_sms_sent', {
            phoneNumber: formattedPhoneNumber,
            username,
          })
          setChangePasswordModalData({
            isOpen: true,
            userId: response.data.userId,
            phoneNumber: formattedPhoneNumber,
          })

          enqueueSnackbar(t('validation.successfullySentSms'), {
            variant: 'success',
          })
          form.setValue('formMode', 'login')
        } catch (error) {
          void op.track('reset_password_sms_error', {
            phoneNumber: formattedPhoneNumber,
            username,
            error: String(error),
          })
          if (!axios.isAxiosError(error)) {
            enqueueSnackbar(t('general.error'))
            return
          }

          const didNotFind = error.response?.status === HttpStatusCode.NotFound
          if (didNotFind) {
            enqueueSnackbar(t('views.login.phoneNotFound'), {
              variant: 'error',
            })
          }
        }
      }

      return
    }

    if (formValues.formMode === 'resetPassword') {
      if (!token || !id) return
      const { newPassword, newPasswordConfirm } = formValues

      const resetPasswordBody = {
        token,
        userId: id,
        password: newPassword,
        repeatPassword: newPasswordConfirm,
      }

      try {
        await resetPassword(resetPasswordBody)
        void op.track('reset_password_by_token_success', { userId: id })
        enqueueSnackbar(t('views.login.passwordReseted'), {
          variant: 'success',
        })

        navigate('/login', { replace: true })
      } catch (error) {
        void op.track('reset_password_by_token_error', {
          userId: id,
          error: String(error),
        })
        if (axios.isAxiosError(error)) {
          const errorMessage: unknown = error.response?.data.developerMessage
          if (typeof errorMessage === 'string') {
            enqueueSnackbar(errorMessage, { variant: 'error' })
            return
          }
        }

        enqueueSnackbar(t('views.login.newPasswordConfirmFailed'), {
          variant: 'error',
        })
      }
    }
  }

  const setIsPasswordValid = useCallback(
    (isValid: boolean) => {
      form.setValue('isPasswordValid', isValid)
      void form.trigger('password')
    },
    [form],
  )

  return (
    <>
      <ForgotUsernameDialog
        isOpen={isForgotUsernameOpen}
        onClose={() => {
          setIsForgotUsernameOpen(false)
        }}
      />

      {changePasswordModalData.userId &&
        changePasswordModalData.phoneNumber && (
          <ChangePasswordBySmsModal
            userId={changePasswordModalData.userId}
            isOpen={changePasswordModalData.isOpen}
            phoneNumber={changePasswordModalData.phoneNumber}
            setIsOpen={isOpen => {
              setChangePasswordModalData(prev => ({ ...prev, isOpen }))
            }}
            onPasswordChangeSucessful={() => {
              form.setValue('formMode', 'login')
            }}
          />
        )}

      <Stack
        gap={2}
        noValidate
        component="form"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Collapse in={form.watch('formMode') === 'forgotPassword'}>
          <Alert sx={{ borderRadius: 2 }} severity="info">
            {t('views.login.resetTitle')}
          </Alert>
        </Collapse>

        {form.watch('formMode') === 'login' && (
          <>
            <TextFieldElement
              required
              autoFocus
              id="login-username"
              name="username"
              variant="outlined"
              placeholder={`${t('views.login.username')} *`}
              inputProps={{ 'aria-label': t('views.login.username') }}
              control={form.control}
              sx={authFieldSx}
            />

            <PasswordElement
              required
              id="login-password"
              name="password"
              variant="outlined"
              placeholder={`${t('views.login.password')} *`}
              inputProps={{ 'aria-label': t('views.login.password') }}
              control={form.control}
              sx={authFieldSx}
            />

            <Stack alignItems="flex-end" sx={{ mt: -0.75 }}>
              <Link
                component="a"
                variant="h6"
                fontSize={15}
                fontWeight={600}
                lineHeight={1}
                letterSpacing="-0.06px"
                textAlign="right"
                sx={{
                  cursor: 'pointer',
                  color: '#9a9f9d',
                  textDecoration: 'none',
                  '&:hover': { color: '#50aa47', textDecoration: 'underline' },
                }}
                onClick={() => {
                  if (form.formState.isSubmitting) return
                  handleFormSublink()
                }}
              >
                {t('views.login.forgotPassword')}
              </Link>
            </Stack>

            <Divider sx={{ color: '#9aa19f', fontSize: 13 }}>
              {t('views.login.orDivider')}
            </Divider>

            <Button
              fullWidth
              type="button"
              variant="outlined"
              startIcon={<GoogleIcon />}
              loading={isSsoRedirecting}
              disabled={form.formState.isSubmitting || isSsoRedirecting}
              onClick={() => {
                setIsSsoRedirecting(true)

                void (async () => {
                  try {
                    const returnPath = searchParams.get('returnURL')
                    const response = await getSsoLoginUrl(
                      getSsoRedirectUri('web'),
                      returnPath ?? undefined,
                    )
                    window.location.href = response.data.url
                  } catch {
                    setIsSsoRedirecting(false)
                    enqueueSnackbar(t('views.login.connectionError'), {
                      variant: 'error',
                    })
                  }
                })()
              }}
              sx={{
                minHeight: 50,
                borderRadius: 2.5,
                borderColor: '#d8dee6',
                color: '#4d5351',
                fontWeight: 700,
                '&:hover': {
                  borderColor: '#50aa47',
                  backgroundColor: 'rgba(80, 170, 71, 0.04)',
                },
              }}
            >
              {t('views.login.signInWithGoogle')}
            </Button>
          </>
        )}

        {form.watch('formMode') === 'forgotPassword' && (
          <>
            <SelectElement
              required
              autoFocus
              control={form.control}
              name="credentialMode"
              label={t('views.user.form.verificationMethod')}
              options={verificationMethodOptions}
              sx={authFieldSx}
            />

            <TextFieldElement
              required
              name="username"
              variant="outlined"
              control={form.control}
              label={t('views.login.username')}
              sx={authFieldSx}
            />

            <Collapse
              unmountOnExit
              in={form.watch('credentialMode') === 'EMAIL'}
              timeout={{ enter: 200, exit: 0 }}
            >
              <TextFieldElement
                fullWidth
                required
                type="email"
                name="email"
                variant="outlined"
                control={form.control}
                label={t('views.login.email')}
                sx={authFieldSx}
              />
            </Collapse>

            <Collapse
              unmountOnExit
              timeout={{ enter: 200, exit: 0 }}
              in={form.watch('credentialMode') === 'PHONE'}
            >
              <PhoneNumberInput
                fullWidthInputs
                autocompleteProps={{
                  required: true,
                  label: t('views.family.phoneCode'),
                  name: 'phoneCode',
                  control: form.control,
                  autocompleteProps: {
                    autoHighlight: true,
                    sx: authFieldSx,
                  },
                }}
                textFieldProps={{
                  required: true,
                  label: t('views.family.phone'),
                  name: 'phoneNumber',
                  control: form.control,
                  sx: authFieldSx,
                }}
              />
            </Collapse>
          </>
        )}

        {form.watch('formMode') === 'resetPassword' && (
          <>
            <PasswordElement
              required
              autoFocus
              name="newPassword"
              variant="outlined"
              label={t('views.login.newPassword')}
              control={form.control}
              sx={authFieldSx}
              onChange={() => {
                if (form.getValues('newPasswordConfirm')) {
                  void form.trigger('newPasswordConfirm')
                }
              }}
            />

            <PasswordElement
              required
              name="newPasswordConfirm"
              variant="outlined"
              label={t('views.login.newPasswordConfirm')}
              control={form.control}
              sx={authFieldSx}
              onChange={() => {
                if (form.getValues('newPassword')) {
                  void form.trigger('newPassword')
                }
              }}
            />

            <Collapse
              unmountOnExit
              in={form.getFieldState('newPassword').isDirty}
            >
              <StrongPasswordCriterias
                password={form.watch('newPassword')}
                setIsPasswordValid={setIsPasswordValid}
              />
            </Collapse>
          </>
        )}

        {form.watch('formMode') !== 'login' && (
          <Stack direction="row" justifyContent="space-between" gap={2}>
            {form.watch('formMode') === 'forgotPassword' && (
              <Link
                component="a"
                variant="body2"
                sx={{
                  cursor: 'pointer',
                  textDecoration: 'none',
                  color: '#50aa47',
                  fontWeight: 700,
                  '&:hover': { textDecoration: 'underline' },
                }}
                onClick={() => {
                  if (form.formState.isSubmitting) return
                  setIsForgotUsernameOpen(true)
                }}
              >
                {t('views.login.forgotUsername')}
              </Link>
            )}
            <Link
              component="a"
              variant="body2"
              sx={{
                ml: 'auto',
                cursor: 'pointer',
                textDecoration: 'none',
                color: '#50aa47',
                fontWeight: 700,
                '&:hover': { textDecoration: 'underline' },
              }}
              onClick={() => {
                if (form.formState.isSubmitting) return
                handleFormSublink()
              }}
            >
              {t('views.login.backLogin')}
            </Link>
          </Stack>
        )}

        <Button
          sx={{
            minHeight: 52,
            mt: 0.5,
            borderRadius: 2.5,
            backgroundColor: '#5cb653',
            boxShadow: 'none',
            textTransform: 'uppercase',
            '&:hover': {
              backgroundColor: '#479f40',
              boxShadow: '0 8px 18px rgba(80, 170, 71, 0.18)',
            },
          }}
          type="submit"
          variant="contained"
          loading={form.formState.isSubmitting}
        >
          <Typography
            variant="h6"
            fontSize={20}
            fontWeight={700}
            lineHeight={1}
            letterSpacing="-0.07px"
            textAlign="center"
            color="#fff"
          >
            {form.watch('formMode') === 'login'
              ? t('views.login.login')
              : t('views.login.resetPassword')}
          </Typography>
        </Button>
      </Stack>
    </>
  )
}

interface ChangePasswordBySmsModalProps {
  isOpen: boolean
  userId: number
  phoneNumber: string

  setIsOpen: (isOpen: boolean) => void
  onPasswordChangeSucessful: () => void
}
function ChangePasswordBySmsModal({
  isOpen,
  userId,
  phoneNumber,
  setIsOpen,
  onPasswordChangeSucessful,
}: ChangePasswordBySmsModalProps) {
  const [t] = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const formId = useId()

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)

  type FormValues = z.infer<typeof formSchema>
  const formSchema = useMemo(() => {
    const requiredError = t('validation.fieldIsRequired')
    const schema = z
      .object({
        verificationCode: z.string().min(6, requiredError),
        isPasswordValid: z.boolean().default(false),
        newPassword: z.string().min(1, requiredError),
        newPasswordConfirm: z.string().min(1, requiredError),

        userId: z.number(),
        phoneNumber: z.string(),
      })
      .superRefine((values, ctx) => {
        const { newPassword, newPasswordConfirm, isPasswordValid } = values

        if (newPassword !== newPasswordConfirm) {
          ctx.addIssue({
            code: 'custom',
            message: t('views.user.form.passwordConfirmFailed'),
            path: ['newPasswordConfirm'],
          })
        }

        if (!isPasswordValid) {
          ctx.addIssue({
            code: 'custom',
            message: t('views.user.form.passwordIsNotStrong'),
            path: ['newPassword'],
          })
          return z.NEVER
        }
      })

    return schema
  }, [t])

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      verificationCode: '',
      isPasswordValid: false,
      newPassword: '',
      newPasswordConfirm: '',
      phoneNumber,
      userId,
    },
  })

  const setIsPasswordValid = useCallback(
    (isValid: boolean) => {
      form.setValue('isPasswordValid', isValid)
      void form.trigger('newPassword')
    },
    [form],
  )

  async function onSubmit(values: FormValues) {
    try {
      const changePasswordBody = {
        userId: values.userId,
        code: values.verificationCode,
        password: values.newPassword,
        repeatPassword: values.newPasswordConfirm,
        phoneNumber: values.phoneNumber,
      }

      await changePasswordBySMSCode(changePasswordBody)
      void op.track('reset_password_by_sms_success', {
        userId: values.userId,
        phoneNumber: values.phoneNumber,
      })
      enqueueSnackbar(t('views.login.passwordReseted'), {
        variant: 'success',
      })

      setIsOpen(false)
      onPasswordChangeSucessful()
    } catch (error) {
      void op.track('reset_password_by_sms_error', {
        userId: values.userId,
        phoneNumber: values.phoneNumber,
        error: String(error),
      })
      if (axios.isAxiosError(error)) {
        const errorMessage: unknown = error.response?.data.developerMessage
        if (typeof errorMessage === 'string') {
          enqueueSnackbar(errorMessage, { variant: 'error' })
          return
        }
      }

      enqueueSnackbar(t('general.error'), { variant: 'error' })
    }
  }

  return (
    <>
      <Modal
        open={isOpen}
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex',
        }}
      >
        <Card
          variant="outlined"
          sx={{
            p: 2,
            outline: 'none',
            width: { xs: '100%', sm: '90%', md: 700 },
          }}
        >
          <CardContent>
            <Divider>
              <Typography gutterBottom variant="h5" textAlign="center">
                {t('views.toolbar.changePassword')}
              </Typography>
            </Divider>

            <Stack
              pt={2}
              gap={2}
              noValidate
              id={formId}
              component="form"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <TextFieldElement
                required
                fullWidth
                autoFocus
                autoComplete="off"
                control={form.control}
                name="verificationCode"
                label={t('views.family.verificationCode')}
                sx={[
                  authFieldSx,
                  theme => ({
                    input: {
                      textAlign: 'center',
                      color: theme.palette.primary.main,
                      bgcolor: theme.palette.background.default,
                    },
                  }),
                ]}
                InputProps={{
                  // @ts-expect-error - InputComponent should receive TextField props
                  inputComponent: CustomPatternFormat,
                }}
              />

              <PasswordElement
                required
                name="newPassword"
                variant="outlined"
                label={t('views.login.newPassword')}
                control={form.control}
                sx={[
                  authFieldSx,
                  { bgcolor: theme => theme.palette.background.default },
                ]}
                onChange={() => {
                  if (form.getValues('newPasswordConfirm')) {
                    void form.trigger('newPasswordConfirm')
                  }
                }}
              />

              <PasswordElement
                required
                name="newPasswordConfirm"
                variant="outlined"
                label={t('views.login.newPasswordConfirm')}
                control={form.control}
                sx={[
                  authFieldSx,
                  { bgcolor: theme => theme.palette.background.default },
                ]}
                onChange={() => {
                  if (form.getValues('newPassword')) {
                    void form.trigger('newPassword')
                  }
                }}
              />

              <Collapse
                unmountOnExit
                in={form.getFieldState('newPassword').isDirty}
              >
                <StrongPasswordCriterias
                  password={form.watch('newPassword')}
                  setIsPasswordValid={setIsPasswordValid}
                />
              </Collapse>
            </Stack>
          </CardContent>
          <CardActions sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              startIcon={<CancelIcon />}
              disabled={form.formState.isSubmitting}
              variant="outlined"
              onClick={() => {
                setIsConfirmationModalOpen(true)
              }}
            >
              {t('general.cancel')}
            </Button>
            <Button
              type="submit"
              form={formId}
              variant="contained"
              endIcon={<LockResetIcon />}
              loading={form.formState.isSubmitting}
            >
              {t('views.login.resetPassword')}
            </Button>
          </CardActions>
        </Card>
      </Modal>

      <Modal
        open={isConfirmationModalOpen}
        onClose={() => {
          setIsConfirmationModalOpen(false)
        }}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Card variant="outlined" sx={{ p: 2 }}>
          <CardContent>
            <Stack
              gap={2}
              alignItems="center"
              sx={{ maxWidth: theme => theme.breakpoints.values.sm }}
            >
              <CardMedia
                image={face}
                component="img"
                alt="Warning icon"
                sx={{ width: 100 }}
              />

              <Divider flexItem>
                <Typography variant="h5">
                  {t('views.exitModal.confirmTitle')}
                </Typography>
              </Divider>

              <Typography
                variant="body1"
                sx={{ color: theme => theme.palette.grey[700] }}
              >
                {t('views.login.exitModalInfo')}
              </Typography>
            </Stack>
          </CardContent>

          <CardActions sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={() => {
                setIsConfirmationModalOpen(false)
                setIsOpen(false)
              }}
            >
              {t('general.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setIsConfirmationModalOpen(false)
              }}
            >
              {t('views.login.continueReset')}
            </Button>
          </CardActions>
        </Card>
      </Modal>
    </>
  )
}

interface CustomPatternFormatProps {
  onChange: (event: { target: { name: string; value: string } }) => void
  name: string
}

const CustomPatternFormat = forwardRef<
  HTMLInputElement,
  CustomPatternFormatProps
>((props, ref) => {
  const { onChange, ...other } = props

  return (
    <PatternFormat
      {...other}
      mask="_"
      getInputRef={ref}
      format="# # # # # #"
      allowEmptyFormatting
      onValueChange={values => {
        onChange({ target: { name: props.name, value: values.value } })
      }}
    />
  )
})
